import {
  Connectivities,
  PimProductT,
  SimSlot,
} from '@lambda/apis/src/dynamoDb/types';
import { Dictionary } from 'lodash';
import { ReebeloLanguage } from '@lambda/reebelo';
import {
  ConnectivitieSkus,
  OfferTree,
  SelectionT,
  SimSlotSkus,
} from '@/lib/collections/catalogue/helpers';
import { DynamicVariantGroup } from './types';
import Variant from '../Variant';
import * as ColorVariant from '../ColorVariant';
import i18n from '@/settings/i18n';
import ColorSwatch from './ColorSwatch';

type DynamicVariantsProps = {
  dynamicVariantGroups: DynamicVariantGroup[];
  selection: SelectionT;
  offerTree: OfferTree;
  product: PimProductT;
  isUnfitProduct: boolean;
  hoverColor: string;
  setHoverColor: React.Dispatch<React.SetStateAction<string>>;
  productColors: string[];
  colorsToCodeMapping: Dictionary<string>;
  setSuggestedVariant: React.Dispatch<React.SetStateAction<string>>;
  suggestedVariant: string;
  language: ReebeloLanguage;
};

export const DynamicVariants: React.FC<DynamicVariantsProps> = ({
  dynamicVariantGroups,
  selection,
  offerTree,
  product,
  isUnfitProduct,
  hoverColor,
  setHoverColor,
  productColors,
  colorsToCodeMapping,
  suggestedVariant,
  setSuggestedVariant,
  language,
}) => {
  const selectedDynamicVariants = dynamicVariantGroups?.map((group) => {
    if (!group) return null;
    const selectedKey = group.name;
    const selectedValue = selection[group?.name as keyof SelectionT];

    return { [selectedKey]: selectedValue };
  });
  const t = i18n.init();

  const flattenedSelectedDynamicVariants = selectedDynamicVariants?.reduce(
    (acc, curr) => ({ ...acc, ...curr }),
    {},
  );

  return (
    <>
      {dynamicVariantGroups.map((group) => (
        <Variant.Group
          title={t(group?.name ?? '')}
          psku={product.psku}
          className="grid grid-cols-3 md:grid-cols-5"
          id={`e2e-product-${group?.name ?? ''}`}
          suggestedVariant={suggestedVariant}
        >
          {group?.variantOptions
            .filter((option) => {
              const isConnectivityGroup =
                group?.name.toLowerCase() === 'connectivity';

              const isSimSlotGroup = group?.name === 'simSlot';

              if (isConnectivityGroup) {
                return offerTree.orderedPath.some((path) =>
                  path
                    .split(',')
                    .find(
                      (p) =>
                        p ===
                        ConnectivitieSkus[language][option as Connectivities],
                    ),
                );
              }

              if (isSimSlotGroup) {
                return offerTree.orderedPath.some((path) =>
                  path.includes(SimSlotSkus[language][option as SimSlot]),
                );
              }

              return offerTree.orderedPath.some((path) =>
                path.split(',').find((p) => p === option),
              );
            })
            .map((_option, i) => {
              const isConnectivityGroup =
                group?.name.toLowerCase() === 'connectivity';
              const isSimSlotGroup = group?.name === 'simSlot';

              const generateOption = () => {
                if (isConnectivityGroup)
                  return ConnectivitieSkus[language][_option as Connectivities];

                if (isSimSlotGroup)
                  return SimSlotSkus[language][_option as SimSlot];

                return _option;
              };

              const option = generateOption();

              return (
                <Variant.Anchor
                  disabled={isUnfitProduct}
                  key={`${option}-${i}`}
                  setSuggestedVariant={setSuggestedVariant}
                  variant={group?.name ?? ''}
                  query={offerTree.getFrom(
                    {
                      ...flattenedSelectedDynamicVariants,
                      [group.name]: option,
                      color: selection.color,
                      serviceProvider: selection.serviceProvider,
                    },
                    selection,
                  )}
                  active={selection[group.name as keyof SelectionT] === option}
                >
                  {option || ''}
                </Variant.Anchor>
              );
            })}
        </Variant.Group>
      ))}
      <ColorVariant.Group
        title={t`Color`}
        psku={product.psku}
        selectedColor={hoverColor !== '' ? hoverColor : selection.color}
        id="e2e-product-color"
        suggestedVariant={suggestedVariant}
      >
        {productColors.sort().map((e) => {
          // if color is not defined fall back to white color
          // if loading placeholder color is grey
          const currentColorCode = product.colorsInHex
            ? product.colorsInHex[colorsToCodeMapping[e]] || '#ffffff'
            : '#cccccc';
          const query = offerTree.getFrom(
            {
              serviceProvider: selection.serviceProvider,
              screenSize: selection.screenSize,
              connectivity: selection.connectivity,
              storage: selection.storage,
              color: e,
            },
            selection,
          );

          return (
            <ColorVariant.Anchor
              disabled={isUnfitProduct}
              key={e}
              query={query}
              suggested={query?.suggested}
              currentColor={e}
              active={selection.color === e}
              onHover={(c) => setHoverColor(c)}
              onHoverOut={() => setHoverColor('')}
              setSuggestedVariant={setSuggestedVariant}
              variant="color"
            >
              <ColorSwatch
                color={currentColorCode}
                active={isUnfitProduct ? false : selection.color === e}
                outOfStock={query == null || isUnfitProduct}
                suggested={query?.suggested}
              />
            </ColorVariant.Anchor>
          );
        })}
      </ColorVariant.Group>
    </>
  );
};
